import {
  BackwardOutlined,
  DownloadOutlined,
  FastForwardOutlined,
  ForwardOutlined,
  LoadingOutlined,
  MoreOutlined,
  MutedOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  SoundOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Menu, Slider, Spin, Tooltip } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useAudioLoader } from "../customHook/AudioLoader";

const MediaAudioContent = ({ file, shortName }) => {
  const {
    loading,
    audioUrl,
    playbackState,
    currentTime,
    duration,
    volume,
    isMuted,
    playbackRate,
    play,
    pause,
    seek,
    setVolumeLevel,
    toggleMute,
    setPlaybackRate,
    download,
  } = useAudioLoader(file?.resourceId);

  const [isShowVolume, setIsShowVolume] = useState(false);
  const volumeRef = useRef(null);

  const maxLength = 30;

  const getShortName = () => {
    const trimmedName = shortName.trim();
    const lastDotIndex = trimmedName.lastIndexOf(".");

    const nameWithoutExtension =
      lastDotIndex !== -1
        ? trimmedName.substring(0, lastDotIndex)
        : trimmedName;

    if (nameWithoutExtension.length > maxLength) {
      return (
        <Tooltip title={nameWithoutExtension} trigger={"hover"} placement="top">
          <h4>{nameWithoutExtension.substring(0, maxLength) + "..."}</h4>
        </Tooltip>
      );
    } else {
      return <h4>{nameWithoutExtension}</h4>;
    }
  };

  const handleClickOutside = (event) => {
    if (volumeRef.current && !volumeRef.current.contains(event.target)) {
      setIsShowVolume(false);
    }
  };

  const isPlaying = playbackState === "playing";

  const togglePlayPause = () => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  };

  const skipBackward = () => {
    const skipAmount = duration >= 3600 ? 60 : duration >= 60 ? 10 : 5;
    seek(Math.max(0, currentTime - skipAmount));
  };

  const skipForward = () => {
    const skipAmount = duration >= 3600 ? 60 : duration >= 60 ? 10 : 5;
    seek(Math.min(duration, currentTime + skipAmount));
  };

  const formatTime = (time) => {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);

    return duration >= 3600
      ? `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
      : duration >= 60
        ? `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
        : duration <= 60
          ? `${seconds.toString().padStart(2, "0")}s`
          : `${seconds.toString().padStart(2, "0")}s`;
  };

  const handleDownload = (e) => {
    download(file?.originalName);
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const menu = (
    <Menu>
      <Menu.Item key="1" icon={<DownloadOutlined />}>
        <button onClick={handleDownload}>Download</button>
      </Menu.Item>
      <Menu.SubMenu
        title="Playback Speed"
        icon={<FastForwardOutlined />}
        placement="topLeft"
      >
        {[0.5, 1, 1.5, 2].map((rate) => (
          <Menu.Item
            key={`playback-${rate}`}
            className={playbackRate === rate ? "bg-[#e0e0e0]" : ""}
            onClick={() => setPlaybackRate(rate)}
          >
            {rate}x
          </Menu.Item>
        ))}
      </Menu.SubMenu>
    </Menu>
  );

  return (
    <div>
      {loading ? (
        <div className="fake-audio-player-card">
          <Spin indicator={<LoadingOutlined className="text-[48px]" />} />
        </div>
      ) : audioUrl ? (
        <div className="audio-player-card">
          <div className="audio-player-header">{getShortName()}</div>

          <Slider
            value={currentTime}
            max={duration}
            onChange={(value) => seek(value)}
            className="time-slider"
            tooltip={{
              placement: "top",
              formatter: (value) => formatTime(value),
            }}
          />
          <div className="time-info">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
          <div className="controls-wrapper">
            <div className="controls-container-center">
              <Button
                shape="circle"
                icon={<BackwardOutlined />}
                className="control-button"
                onClick={skipBackward}
              />
              <Button
                shape="circle"
                icon={
                  isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />
                }
                className="control-button"
                onClick={togglePlayPause}
              />
              <Button
                shape="circle"
                icon={<ForwardOutlined />}
                className="control-button"
                onClick={skipForward}
              />
            </div>
            <div className="controls-container-end">
              <div
                className="volume-container"
                // ref={volumeRef}
                onMouseEnter={() => setIsShowVolume(true)}
                onMouseLeave={() => setIsShowVolume(false)}
              >
                <Button
                  shape="circle"
                  icon={
                    isMuted || volume === 0 ? (
                      <MutedOutlined />
                    ) : (
                      <SoundOutlined />
                    )
                  }
                  className="control-button"
                  onClick={toggleMute}
                />
                {isShowVolume && (
                  <Slider
                    value={isMuted ? 0 : volume * 100}
                    onChange={(value) => setVolumeLevel(value / 100)}
                    className="volume-slider"
                  />
                )}
              </div>
              <Dropdown overlay={menu} trigger={["click"]} placement="topLeft">
                <Button
                  shape="circle"
                  icon={<MoreOutlined />}
                  className="more-options"
                />
              </Dropdown>
            </div>
          </div>
        </div>
      ) : (
        <div className="fake-audio-player-card">
          <p>No media data available</p>
        </div>
      )}
    </div>
  );
};

export default MediaAudioContent;
